import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { handleOAuthCallback, setSession } = useAuth();
  const [status, setStatus] = useState('processing');
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Prevent double processing (React StrictMode or re-mounts)
    if (hasProcessedRef.current) return;
    const processCallback = async () => {
      const hash = window.location.hash;

      // Discord OAuth: backend redirects here with #discord_token=sess_xxx
      const discordMatch = hash.match(/discord_token=([^&]+)/);
      if (discordMatch) {
        hasProcessedRef.current = true;
        const sessToken = discordMatch[1];
        window.history.replaceState(null, '', window.location.pathname);
        setStatus('authenticating');
        try {
          const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
          const axios = (await import('axios')).default;
          const meRes = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${sessToken}` },
          });
          localStorage.setItem('user_data', JSON.stringify(meRes.data));
          setSession(sessToken, meRes.data);
          setStatus('redirecting');
          await new Promise(resolve => setTimeout(resolve, 100));
          if (meRes.data && meRes.data.profile_complete === false) {
            navigate('/complete-profile', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        } catch (error) {
          console.error('Discord callback error:', error);
          setStatus('error');
          setTimeout(() => navigate('/login', { replace: true }), 2500);
        }
        return;
      }

      const sessionIdMatch = hash.match(/session_id=([^&]+)/);

      if (!sessionIdMatch) {
        // No session_id — check if already logged in
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/login', { replace: true });
        }
        return;
      }

      // Mark as processing immediately
      hasProcessedRef.current = true;
      const sessionId = sessionIdMatch[1];
      
      // Clear hash immediately to prevent re-processing on re-mount
      window.history.replaceState(null, '', window.location.pathname);
      
      setStatus('authenticating');

      try {
        const userData = await handleOAuthCallback(sessionId);

        setStatus('redirecting');
        await new Promise(resolve => setTimeout(resolve, 100));

        if (userData && userData.profile_complete === false) {
          navigate('/complete-profile', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        // If token was saved by a previous successful call, proceed anyway
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
          navigate('/dashboard', { replace: true });
          return;
        }
        setStatus('error');
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2500);
      }
    };

    processCallback();
  }, [handleOAuthCallback, navigate]);

  return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-[#2979FF] animate-spin mx-auto mb-4" />
        <p className="text-slate-400">
          {status === 'processing' && 'Elaborazione...'}
          {status === 'authenticating' && 'Autenticazione in corso...'}
          {status === 'redirecting' && 'Reindirizzamento...'}
          {status === 'error' && 'Autenticazione fallita. Reindirizzamento al login...'}
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;
